/**
 * Make sure the app shell knows about whatever project the URL is pointing at.
 *
 * The shell's chrome (the switcher's name and environment marker, the rail,
 * the tab title) all resolve the current project out of the cached list the
 * auth guard fetched once at sign-in. That list is the account's PROJECTS, and
 * a staging environment deliberately is not one of them: it rides along on its
 * parent's payload so the projects screen does not show every app twice.
 *
 * Which left a real hole. Open a staging environment's own page and the chrome
 * found nothing for that slug, so it fell back to "All projects" and the
 * STAGING marker could never render at all. That marker is the one thing
 * telling somebody they are about to act on the environment in front of their
 * live app rather than on the live app itself. It looked right in the demo
 * build only because every mock project is in that store.
 *
 * So: if the URL names a project the cache has not got, fetch that one and put
 * it in. Written for the general case rather than for staging, because the same
 * hole opens for a project connected in another tab.
 */

import { useEffect } from 'react';

import { getProject } from '../api';
import { useSessionStore } from '../auth/session';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

export function useResolveProject(slug: string | null): void {
  const known = useSessionStore((s) => (slug ? s.projects.some((p) => p.slug === slug) : true));
  const upsertProject = useSessionStore((s) => s.upsertProject);
  const status = useSessionStore((s) => s.status);

  useEffect(() => {
    // The demo build keeps every project in its own store, and there is no
    // account to fetch one against.
    if (USE_MOCK || !slug || known || status !== 'signed-in') return;
    let cancelled = false;
    getProject(slug)
      .then((project) => {
        if (!cancelled && project) upsertProject(project);
      })
      .catch(() => {
        // A slug that resolves to nothing is the 404 the screen itself will
        // render. The chrome simply stays as it is rather than guessing.
      });
    return () => {
      cancelled = true;
    };
  }, [slug, known, status, upsertProject]);
}
